import { 
  collection, addDoc, query, where, orderBy, getDocs,
  onSnapshot, updateDoc, doc, writeBatch, serverTimestamp 
} from 'firebase/firestore';
import { databasePromise } from 'configuration';

export const notificationService = {
  create: async (userId, notificationData) => {
    try {
      const db = await databasePromise;
      await addDoc(collection(db, 'notifications'), {
        userId,
        ...notificationData,
        read: false,
        cleared: false,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  },

  // Synchronous return with cancelled flag to prevent listener leak
  // if component unmounts before databasePromise resolves.
  // Previously async — callers that didn't await got a Promise instead
  // of the unsubscribe function, causing silent listener leaks.
  subscribe: (userId, target, callback) => {
    let cancelled = false;
    let realUnsub = () => {};

    databasePromise.then((db) => {
      if (cancelled) return;
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('target', '==', target),
        where('cleared', '==', false),
        orderBy('createdAt', 'desc')
      );

      realUnsub = onSnapshot(q,
        (snapshot) => {
          const notifications = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          callback(notifications);
        },
        (error) => {
          console.error('Error subscribing to notifications:', error);
          callback([]);
        }
      );
    });

    return () => {
      cancelled = true;
      realUnsub();
    };
  },

  markAsRead: async (notificationId) => {
    try {
      const db = await databasePromise;
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  markAllAsRead: async (userId, target) => {
    try {
      const db = await databasePromise;
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('target', '==', target),
        where('read', '==', false),
        where('cleared', '==', false)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return;
      
      const batch = writeBatch(db);
      snapshot.docs.forEach(doc => {
        batch.update(doc.ref, { read: true, readAt: serverTimestamp() });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error marking all as read:', error);
      throw error;
    }
  },

  clear: async (notificationId) => {
    try {
      const db = await databasePromise;
      await updateDoc(doc(db, 'notifications', notificationId), {
        cleared: true,
        clearedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error clearing notification:', error);
      throw error;
    }
  },

  clearAll: async (userId, target) => {
    try {
      const db = await databasePromise;
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('target', '==', target),
        where('cleared', '==', false)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return;
      
      const batch = writeBatch(db);
      snapshot.docs.forEach(doc => {
        batch.update(doc.ref, { cleared: true, clearedAt: serverTimestamp() });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  }
};